import { useGetCategoriesPaginated } from "@/hooks/services/use-categories";
import {
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  MRT_ToggleFiltersButton,
} from "material-react-table";
import { useMemo } from "react";

import { APIPaginatedTable } from "@/components/common/api-paginated-table";
import type { APIPaginatedTableState } from "@/components/common/api-paginated-table/states";
import type { Category } from "@/types/services/api-types/category-types";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

const createCategoriesFilters = (columnFilters: MRT_ColumnFiltersState) => {
  const filters: {
    name?: string;
  } = {};

  const getTrimmedString = (value: unknown) =>
    typeof value === "string" ? value.trim() : "";

  columnFilters.forEach((filter) => {
    const colName = filter.id.split(".")[1] ?? filter.id;
    const trimmedValue = getTrimmedString(filter.value);
    if (trimmedValue === "") return;

    if (colName === "name") {
      filters.name = trimmedValue;
    }
  });

  return filters;
};

interface CategoriesTableProps {
  onCategoryClick: (category: Category) => void;
  onDeleteClick: (category: Category) => void;
}

const CategoriesTable = ({
  onCategoryClick,
  onDeleteClick,
}: CategoriesTableProps) => {
  const columns = useMemo<MRT_ColumnDef<Category>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row, renderedCellValue }) => {
          const category = row.original;
          return (
            <button
              onClick={() => onCategoryClick?.(category)}
              className="text-purple-500 hover:underline cursor-pointer"
            >
              {renderedCellValue}
            </button>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => {
          const category = row.original;
          return (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onDeleteClick(category)}
            >
              <Trash2 />
            </Button>
          );
        },
      },
    ],
    [onCategoryClick, onDeleteClick]
  );

  return (
    <>
      <APIPaginatedTable
        columns={columns}
        reactQuery={useGetCategoriesPaginated}
        reactQueryFilters={(state: APIPaginatedTableState) => {
          const filters = createCategoriesFilters(state.columnFilters);

          return {
            page: state.pagination.pageIndex,
            size: state.pagination.pageSize,
            ...filters,
          };
        }}
        onRenderToolbarInternalActions={({ table }) => (
          <>
            <MRT_ToggleFiltersButton table={table} />
          </>
        )}
        titleTopic="service categories"
        contentTopic="service category"
      />
    </>
  );
};

export default CategoriesTable;
